import { useEffect, useRef, useState } from "react";
import { Menu, Search, Globe, UserCircle, LogOut, ChevronDown, LayoutDashboard, User } from "lucide-react";
import { Link } from "react-router-dom";
import SkyLogo from "./SkyLogo";
import translations from "../locals/translations.json";
import { useLanguage } from "../context/LanguageContext";
import { useAuth } from "../context/AuthContext";
import { io } from "socket.io-client";
import { useNavigate } from "react-router-dom";
import skylogo from "../assets/skyAutologo.png";
import CurrencyDropdown from "../components/common/CurrencyDropdown";
import NotificationBell from "./NotificationBell";

const NewHeader = () => {
  const [isCurrencyOpen, setIsCurrencyOpen] = useState(false);
  const [isLanguageOpen, setIsLanguageOpen] = useState(false);
  const [currency, setCurrency] = useState("USD");
  const { translate, language, setLanguage } = useLanguage();
  const { isAuthenticated, user, logout } = useAuth();
  const isRTL = language === "ar";
  const socketRef = useRef(null);
  const [showBuyNowButton, setShowBuyNowButton] = useState(false);
  const [isMoreOpen, setIsMoreOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const navigate = useNavigate();


  // Close dropdowns on click outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest(".currency-dropdown")) setIsCurrencyOpen(false);
      if (!event.target.closest(".language-dropdown")) setIsLanguageOpen(false);
      if (!event.target.closest(".more-dropdown")) setIsMoreOpen(false);
      if (!event.target.closest(".user-menu-dropdown")) setIsUserMenuOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const mainNavItems = [
    { key: "vehicles", href: "/vehicles", label: "Vehicles" },
    { key: "auctions", href: "/auctions", label: "Auction" },
    { key: "buyers", href: "/buyer", label: "Buyer" },
    { key: "sellers", href: "/seller", label: "Seller" },
  ];

  const moreNavItems = [
    { key: "contact", href: "/contact", label: "Contact Us" },
    { key: "about", href: "/about-us", label: "About Us" },
    { key: "blog", href: "/blog", label: "Blog" },
  ];

  const currentLanguageInfo = {
    en: { label: "English", flag: "us" },
    ar: { label: "العربية", flag: "sa" },
  };

  // Socket connection logic
  useEffect(() => {
    const authUser = localStorage.getItem("authUser");
    const token = authUser ? JSON.parse(authUser).token : null;
    const socketURL = process.env.REACT_APP_SOCKET_URL || "http://localhost:5051";

    const socket = io(socketURL, {
      auth: token ? { token } : undefined,
      transports: ["websocket", "polling"],
      path: "/socket.io/",
      timeout: 20000,
      reconnection: true,
      reconnectionAttempts: 10,
      reconnectionDelay: 1000,
      forceNew: true,
    });

    socketRef.current = socket;

    socket.on("connect", () => {
      socket.emit("joinBuyNow");
    });

    socket.on("connect_error", (err) => {
      console.error("Socket connect_error:", err?.message || err);
    });

    socket.on("buyNowListingUpdated", (data) => {
      if (data.action === "SOLD_FROM_BUYNOW") {
        setShowBuyNowButton(data.buyNowVehiclesCount > 0);
      }
      if (
        authUser &&
        JSON.parse(authUser).id != data.userId &&
        window.location.pathname === "/buynow/vehicles"
      ) {
        window.location.reload();
      }
    });

    socket.on("initialBuyNowCounts", (data) => {
      if (data.action === "INIT") {
        setShowBuyNowButton(data.buyNowVehiclesCount > 0);
      }
    });

    return () => {
      try {
        if (socketRef.current?.connected) {
          socketRef.current.emit("leaveBuyNow");
        }
        socketRef.current?.off("buyNowListingUpdated");
        socketRef.current?.disconnect();
      } catch (e) {
        // Ensure unmount does not throw
      }
    };
  }, []);

  const handleSearch = (e) => {
    if (e.key === "Enter" && searchQuery.trim()) {
      navigate(`/vehicles?search=${encodeURIComponent(searchQuery.trim())}`);
      setSearchQuery("");
      setIsMoreOpen(false);
    }
  };

  const handleSearchClick = () => {
    if (searchQuery.trim()) {
      navigate(`/vehicles?search=${encodeURIComponent(searchQuery.trim())}`);
      setSearchQuery("");
      setIsMoreOpen(false);
    }
  };

  useEffect(() => {
    document.documentElement.dir = isRTL ? "rtl" : "ltr";
    document.documentElement.lang = language;
  }, [language, isRTL]);

  return (




    <header className=" sticky top-0 left-0 right-0 w-full z-50 border-b border-black/10 px-3 lg:px-6 py-3 bg-[#0A0C10] backdrop-blur-md" dir={isRTL ? "rtl" : "ltr"}>
      {/* <header className=" sticky top-0 left-0 right-0 w-full z-50 border-b border-black/10 px-3 lg:px-6 py-3 bg-white backdrop-blur-md" dir={isRTL ? "rtl" : "ltr"}> */}
      <div className=" max-w-7xl mx-auto flex items-center justify-between gap-4">
        {/* Logo Section */}
        <div className="flex items-center gap-3 shrink-0">
          <Link to="/" className="flex items-center gap-3">
            <div className="text-primary">
              <img
                src={skylogo}
                alt="Sky Auto Trade"
                className="w-24 sm:w-28 xl:w-32 2xl:w-48 h-auto"
              />
            </div>
          </Link>
        </div>

        {/* CENTER — Desktop Nav + Menu */}
        <div className="hidden lg:flex items-center gap-8 flex-1 justify-center">

          {/* Desktop Navigation */}
          <nav className="flex items-center gap-8">
            {mainNavItems.map((item) => (
              <Link
                key={item.key}
                to={item.href}
                // className="text-sm font-semibold text-black hover:text-primary transition-colors whitespace-nowrap"
                className="text-sm font-semibold text-white/90 hover:text-primary transition-colors whitespace-nowrap"
              >
                {translations[language]?.[item.key] || item.label}
              </Link>
            ))}

            {showBuyNowButton && (
              <Link
                to="/buynow/vehicles"
                className="bg-yellow-500 text-black px-4 py-2 rounded font-medium animate-pulse"
              >
                {translate("buyNow")}
              </Link>
            )}
          </nav>

          {/* Desktop Menu Button */}
          <div className="relative group more-dropdown">
            <button
              className="flex items-center justify-center p-2 text-white/90 hover:text-primary transition-colors"
              onClick={() => setIsMoreOpen(!isMoreOpen)}
            >
              <Menu className="w-5 h-5" />
            </button>
            {/* Dropdown menu positioned right under the hamburger icon */}
            <div className={`dropdown-menu ${isMoreOpen ? 'block' : 'hidden'} absolute left-0 top-full mt-2 w-48 bg-[#0F172A] border border-white/10 rounded-xl shadow-2xl py-2 z-50`}>
              {/* Mobile Search (sm:hidden) */}
              <div className="sm:hidden px-4 py-2 border-b border-white/10 mb-2">
                <div className="relative">
                  <button onClick={handleSearchClick} className="absolute left-2 top-1/2 -translate-y-1/2 text-white/40 hover:text-primary transition-colors">
                    <Search className="w-3 h-3" />
                  </button>
                  <input
                    className="w-full bg-gray-100 border border-white/20 text-black rounded py-1 pl-7 pr-2 text-[10px] outline-none"
                    placeholder={translations[language]?.newHome?.hero?.searchPlaceholder || "Search..."}
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onKeyDown={handleSearch}
                  />
                </div>
              </div>

              {/* Main Nav Items (Mobile Only) */}
              <div className="lg:hidden border-b border-white/10 mb-2 pb-2">
                {mainNavItems.map((item) => (
                  <Link
                    key={item.key}
                    to={item.href}
                    className="block w-full text-left px-4 py-2 text-sm text-white/70 hover:bg-primary hover:text-white"
                    onClick={() => setIsMoreOpen(false)}
                  >
                    {translations[language]?.[item.key] || item.label}
                  </Link>
                ))}
              </div>

              {/* More Nav Items */}
              {moreNavItems.map((item) => (
                <Link
                  key={item.key}
                  to={item.href}
                  className="block w-full text-left px-4 py-2 text-sm text-white/70 hover:bg-primary hover:text-white"
                  onClick={() => setIsMoreOpen(false)}
                >
                  {translations[language]?.[item.key] || item.label}
                </Link>
              ))}

              {/* Mobile Currency/Lang (sm:hidden) */}
              <div className="sm:hidden border-t border-white/10 mt-2 pt-2 px-4 flex gap-2 justify-between">
                <button onClick={() => setCurrency(currency === 'USD' ? 'AED' : 'USD')} className="text-xs text-white/70 hover:text-white font-bold">{currency}</button>
                <button onClick={() => setLanguage(language === 'en' ? 'ar' : 'en')} className="text-xs text-white/70 hover:text-white font-bold uppercase">{language}</button>
              </div>
            </div>
          </div>

        </div>

        {/* Right Section */}
        <div className="flex items-center gap-4 shrink-0">
          {/* Desktop Navigation item*/}
          {/* <nav className="hidden lg:flex items-center gap-8">
            {mainNavItems.map((item) => (
              <Link
                key={item.key}
                to={item.href}
                className="text-sm font-semibold text-black hover:text-primary transition-colors whitespace-nowrap"
              >
                {translations[language]?.[item.key] || item.label}
              </Link>
            ))}
         {showBuyNowButton && (
  <>
    <style>
      {`
        @keyframes blink {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.6; }
        }
      `}
    </style>

    <Link
      to="/buynow/vehicles"
      className="buy-now-header-button bg-yellow-500 text-black px-4 py-2 rounded font-medium"
      style={{ animation: "blink 1s infinite" }}
    >
      {translate("buyNow")}
    </Link>
  </>
)}
          </nav> */}

          {/* More Menu (Hamburger) */}
          <div className="relative group more-dropdown lg:hidden">
            <button
              className="flex lg:hidden  items-center justify-center p-2 text-black hover:text-primary transition-colors"
              onClick={() => setIsMoreOpen(!isMoreOpen)}
            >
              <Menu className="w-5 h-5" />
            </button>

            {/* <div className={`dropdown-menu ${isMoreOpen ? 'block' : 'hidden'} absolute left-1/2 -translate-x-1/2 top-full mt-2 w-48 bg-gray-100 border border-black/10 rounded-xl shadow-2xl py-2 z-50`}> */}
            <div className={`dropdown-menu ${isMoreOpen ? 'block' : 'hidden'} absolute left-1/2 -translate-x-1/2 top-full mt-2 w-48 bg-[#0F172A] border border-white/10 rounded-xl shadow-2xl py-2 z-50`}>
              {/* Mobile Search (sm:hidden) */}
              <div className="sm:hidden px-4 py-2 border-b border-white/10 mb-2">
                <div className="relative">
                  <button onClick={handleSearchClick} className="absolute left-2 top-1/2 -translate-y-1/2 text-white/40 hover:text-primary transition-colors">
                    {/* <button onClick={handleSearchClick} className="absolute left-2 top-1/2 -translate-y-1/2 text-white/40 hover:text-primary transition-colors"> */}
                    <Search className="w-3 h-3" />
                  </button>
                  <input
                    className="w-full bg-gray-100 border border-white/20 text-black  rounded py-1 pl-7 pr-2 text-[10px]  outline-none"
                    placeholder={translations[language]?.newHome?.hero?.searchPlaceholder || "Search..."}
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onKeyDown={handleSearch}
                  />
                </div>
              </div>

              {/* Main Nav Items (Mobile Only) */}
              <div className="lg:hidden border-b border-white/10 mb-2 pb-2">
                {mainNavItems.map((item) => (
                  <Link
                    key={item.key}
                    to={item.href}
                    // className="block w-full text-left px-4 py-2 text-sm text-black/70 hover:bg-primary hover:text-white"
                    className="block w-full text-left px-4 py-2 text-sm text-white/70 hover:bg-primary hover:text-white"
                    //                 onClick={() => setIsMoreOpen(false)}
                    onClick={() => setIsMoreOpen(false)}
                  >
                    {translations[language]?.[item.key] || item.label}
                  </Link>
                ))}
              </div>

              {/* More Nav Items */}
              {moreNavItems.map((item) => (
                <Link
                  key={item.key}
                  to={item.href}
                  // className="block w-full text-left px-4 py-2 text-sm text-black/70 hover:bg-primary hover:text-white"
                  className="block w-full text-left px-4 py-2 text-sm text-white/70 hover:bg-primary hover:text-white"
                  onClick={() => setIsMoreOpen(false)}
                >
                  {translations[language]?.[item.key] || item.label}
                </Link>
              ))}

              {/* Mobile Currency/Lang (sm:hidden) */}
              <div className="sm:hidden border-t border-white/10 mt-2 pt-2 px-4 flex gap-2 justify-between">
                <button onClick={() => setCurrency(currency === 'USD' ? 'AED' : 'USD')} className="text-xs text-white/70 hover:text-white font-bold">{currency}</button>
                <button onClick={() => setLanguage(language === 'en' ? 'ar' : 'en')} className="text-xs text-white/70 hover:text-white font-bold uppercase">{language}</button>
              </div>
            </div>
          </div>

          {/* Search Bar */}
          {/* <div className="relative hidden sm:block">
            <button onClick={handleSearchClick} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40 hover:text-primary transition-colors">
              <Search className="w-4 h-4" />
            </button>
            <input
              className="w-[240px] bg-gray-100 border border-white/20 text-black placeholder:text-black rounded-lg py-2 pl-9 pr-3 text-xs focus:ring-1 focus:ring-primary focus:border-transparent  outline-none transition-all"
              placeholder={translations[language]?.newHome?.hero?.searchPlaceholder || "Search luxury vehicles..."}
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={handleSearch}
            />
          </div> */}
          <div className="relative hidden sm:block">
            <button onClick={handleSearchClick} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40 hover:text-primary transition-colors">
              <Search className="w-4 h-4" />
            </button>
            <input
              className="w-[240px] bg-white/5 border border-white/10 rounded-lg py-2 pl-9 pr-3 text-xs focus:ring-1 focus:ring-primary focus:border-transparent placeholder:text-white/30 text-white outline-none transition-all"
              placeholder={translations[language]?.newHome?.hero?.searchPlaceholder || "Search luxury vehicles..."}
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={handleSearch}
            />
          </div>

          {/* Currency Selector */}
          {/* <div className="relative group currency-dropdown hidden sm:block">
            <button
              className="flex items-center gap-2 px-3 py-2 text-xs font-bold text-black bg-gray-100 hover:bg-gray-200 border border-black/10 rounded-lg border  transition-all uppercase"
              onClick={() => setIsCurrencyOpen(!isCurrencyOpen)}
            >
              <div className="w-4 h-4 rounded-full border border-black/30 flex items-center justify-center text-[10px]">$</div>
              {currency}
            </button>
            <div className={`dropdown-menu ${isCurrencyOpen ? 'block' : 'hidden'} absolute right-0 top-full mt-2 w-24 bg-gray-100 border border-black/10 rounded-xl shadow-2xl py-2 z-50`}>
              {["USD", "AED", "IQD"].map((curr) => (
                <button
                  key={curr}
                  onClick={() => {
                    setCurrency(curr);
                    setIsCurrencyOpen(false);
                  }}
                  className="block w-full text-left px-4 py-2 text-xs text-black/70 hover:bg-primary hover:text-white"
                >
                  {curr}
                </button>
              ))}
            </div>
          </div> */}
          <CurrencyDropdown />

          {/* Language Selector */}
          {/* <div className="relative group language-dropdown hidden sm:block">
            <button
              className="flex items-center gap-2 px-3 py-2 text-xs font-bold text-black bg-gray-100 hover:bg-gray-200 border border-black/10 rounded-lg  transition-all"
              onClick={() => setIsLanguageOpen(!isLanguageOpen)}
            >
              <Globe className="w-4 h-4" />
              <span className="hidden lg:inline">{currentLanguageInfo[language]?.label || "English"}</span>
              <img
                src={`https://flagcdn.com/${currentLanguageInfo[language]?.flag || 'us'}.svg`}
                alt='Flag'
                className='w-4 h-3 object-cover rounded-[1px]'
              />
              <ChevronDown className="w-3 h-3 text-black/50" />
            </button>
            <div className={`dropdown-menu ${isLanguageOpen ? 'block' : 'hidden'} absolute right-0 top-full mt-2 w-32 bg-gray-100 border border-black/10 rounded-xl shadow-2xl py-2 z-50`}>
              {Object.keys(currentLanguageInfo).map((langCode) => (
                <button
                  key={langCode}
                  onClick={() => {
                    setLanguage(langCode);
                    setIsLanguageOpen(false);
                  }}
                  className="flex items-center gap-2 w-full text-left px-4 py-2 text-xs text-black/70 hover:bg-primary hover:text-white"
                >
                  <img
                    src={`https://flagcdn.com/${currentLanguageInfo[langCode].flag}.svg`}
                    alt={currentLanguageInfo[langCode].label}
                    className="w-4 h-3 object-cover rounded-[1px]"
                  />
                  {currentLanguageInfo[langCode].label}
                </button>
              ))}
            </div>
          </div> */}
          <div className="relative group language-dropdown hidden sm:block">
            <button
              className="flex items-center gap-2 px-3 py-2 text-xs font-bold text-white bg-card  rounded-lg border border-white/10 transition-all"
              onClick={() => setIsLanguageOpen(!isLanguageOpen)}
            >
              {/* <Globe className="w-4 h-4" /> */}
              <span className="hidden lg:inline">{currentLanguageInfo[language]?.label || "English"}</span>
              <img
                src={`https://flagcdn.com/${currentLanguageInfo[language]?.flag || 'us'}.svg`}
                alt='Flag'
                className='w-4 h-3 object-cover rounded-[1px]'
              />
              <ChevronDown className="w-3 h-3 text-white/50" />
            </button>
            <div className={`dropdown-menu ${isLanguageOpen ? 'block' : 'hidden'} absolute right-0 top-full mt-2 w-32 bg-card border border-white/10 rounded-xl shadow-2xl py-2 z-50`}>
              {Object.keys(currentLanguageInfo).map((langCode) => (
                <button
                  key={langCode}
                  onClick={() => {
                    setLanguage(langCode);
                    setIsLanguageOpen(false);
                  }}
                  className="flex items-center gap-2 w-full text-left px-4 py-2 text-xs text-white/70 hover:bg-primary hover:text-white"
                >
                  <img
                    src={`https://flagcdn.com/${currentLanguageInfo[langCode].flag}.svg`}
                    alt={currentLanguageInfo[langCode].label}
                    className="w-4 h-3 object-cover rounded-[1px]"
                  />
                  {currentLanguageInfo[langCode].label}
                </button>
              ))}
            </div>
          </div>
          {/* User Profile / Login */}
          <div className="relative group user-menu-dropdown pl-2">
            {/* <button
              className="flex items-center gap-2 text-black hover:text-primary transition-colors"
              onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
            >
              <div className="hidden 2xl:flex flex-col items-end">
                <span className="text-[10px] text-black/50 leading-none">Welcome</span>
                <span className="text-xs font-bold truncate max-w-[100px]">
                  {isAuthenticated ? (user?.username || user?.name || "User") : "Guest"}
                </span>
              </div>
              <ChevronDown className="w-3 h-3 text-black/50 sm:block hidden" />
              <div className="size-9  rounded-full bg-gray-200 border border-black/10 flex items-center justify-center overflow-hidden">
                <UserCircle className="w-5 h-5 text-black" />
              </div>
            </button> */}
            <button
              className="flex items-center gap-2 text-white/90 hover:text-primary transition-colors"
              onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
            >
              <div className="hidden 2xl:flex flex-col items-end">
                <span className="text-[10px] text-white/50 leading-none">Welcome</span>
                <span className="text-xs font-bold truncate max-w-[100px]">
                  {isAuthenticated ? (user?.username || user?.name || "User") : "Guest"}
                </span>
              </div>
              <ChevronDown className="hidden 2xl:block w-3 h-3 text-white/50" />
              <div className="size-9 rounded-full bg-white/10 border border-white/10 flex items-center justify-center overflow-hidden">
                {user?.profileImgUrl ? (
                  <img
                    src={user.profileImgUrl}
                    alt="Profile"
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <UserCircle className="w-5 h-5 text-white" />
                )}
              </div>
            </button>
            {/* Dropdown */}
            {/* <div className={`dropdown-menu ${isUserMenuOpen ? 'block' : 'hidden'} absolute right-0 top-full mt-2 w-48 bg-gray-100 border border-black/10 rounded-xl shadow-2xl py-2 z-50`}>
              {isAuthenticated ? (
                <>
                  <Link
                    to="/dashboard"
                    className="flex items-center gap-2 px-4 py-2 text-sm text-black/70 hover:bg-primary hover:text-white"
                    onClick={() => setIsUserMenuOpen(false)}
                  >
                    <LayoutDashboard className="w-4 h-4" />
                    {translations[language]?.header?.dashboard || "Dashboard"}
                  </Link>
                  <Link
                    to="/profile"
                    className="flex items-center gap-2 px-4 py-2 text-sm text-black/70 hover:bg-primary hover:text-white"
                    onClick={() => setIsUserMenuOpen(false)}
                  >
                    <User className="w-4 h-4" />
                    {translations[language]?.header?.profile || "Profile"}
                  </Link>
                  <div className="border-t border-black/10 my-1"></div>
                  <button
                    onClick={() => {
                      logout();
                      setIsUserMenuOpen(false);
                    }}
                    className="flex w-full items-center gap-2 px-4 py-2 text-sm text-red-400 hover:bg-red-500/10 hover:text-red-300"
                  >
                    <LogOut className="w-4 h-4" />
                    {translations[language]?.header?.logout || "Logout"}
                  </button>
                </>
              ) : (
                <>
                  <Link
                    to="/login"
                    className="flex items-center gap-2 px-4 py-2 text-sm text-black/70 hover:bg-primary hover:text-white"
                    onClick={() => setIsUserMenuOpen(false)}
                  >
                    <User className="w-4 h-4" />
                    Login
                  </Link>
                  <Link
                    to="/register"
                    className="flex items-center gap-2 px-4 py-2 text-sm text-black/70 hover:bg-primary hover:text-white"
                    onClick={() => setIsUserMenuOpen(false)}
                  >
                    <UserCircle className="w-4 h-4" />
                    Register
                  </Link>
                </>
              )}
            </div> */}
            <div className={`dropdown-menu ${isUserMenuOpen ? 'block' : 'hidden'} absolute right-0 top-full mt-2 w-48 bg-card border border-white/10 rounded-xl shadow-2xl py-2 z-50`}>
              {isAuthenticated ? (
                <>
                  <Link
                    to="/dashboard"
                    className="flex items-center gap-2 px-4 py-2 text-sm text-white/70 hover:bg-primary hover:text-white"
                    onClick={() => setIsUserMenuOpen(false)}
                  >
                    <LayoutDashboard className="w-4 h-4" />
                    {translations[language]?.header?.dashboard || "Dashboard"}
                  </Link>
                  <Link
                    to="/profile"
                    className="flex flex-col px-4 py-2 text-sm text-white/70 hover:bg-primary hover:text-white"
                    onClick={() => setIsUserMenuOpen(false)}
                  >
                    <div className="flex items-center gap-2">
                      <User className="w-4 h-4" />
                      {translations[language]?.header?.profile || "Profile"}
                    </div>

                    {/* Client ID stacked below */}
                    {user?.clientId && (
                      <span className="px-6 text-xs text-white/50 mt-1">
                        {user.clientId}
                      </span>
                    )}
                  </Link>
                  {/* Display clientId below Profile */}
                  <div className="border-t border-white/10 my-1"></div>
                  <button
                    onClick={() => {
                      logout();
                      setIsUserMenuOpen(false);
                    }}
                    className="flex w-full items-center gap-2 px-4 py-2 text-sm text-red-400 hover:bg-red-500/10 hover:text-red-300"
                  >
                    <LogOut className="w-4 h-4" />
                    {translations[language]?.header?.logout || "Logout"}
                  </button>
                </>
              ) : (
                <>
                  <Link
                    to="/login"
                    className="flex items-center gap-2 px-4 py-2 text-sm text-white/70 hover:bg-primary hover:text-white"
                    onClick={() => setIsUserMenuOpen(false)}
                  >
                    <User className="w-4 h-4" />
                    Login
                  </Link>
                  <Link
                    to="/register"
                    className="flex items-center gap-2 px-4 py-2 text-sm text-white/70 hover:bg-primary hover:text-white"
                    onClick={() => setIsUserMenuOpen(false)}
                  >
                    <UserCircle className="w-4 h-4" />
                    Register
                  </Link>
                </>
              )}
            </div>
          </div>

          <NotificationBell />
        </div>
      </div>
    </header>




  );
};

export default NewHeader;
